/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FC, Fragment, useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Accordion, AccordionDetails, AccordionSummary, Button, FormControl, Grid, MenuItem, Typography } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { useStyles } from './styles';
import { useApiError, useAppData, useLoading } from '../../providers';
import { useCommonStyles } from '../../styles';
import { trackAmplitudeData } from '../../services/amplitudeService';
import { createDefaultLink, getAllClientLinks, getAllSubPartnershipLinks, getClientLinks, getClientLinkSetup, getSubPartnershipLinks, updateClientLinkName, updatePartnerLinkName } from './RegistrationLinksService';
import { CopyIcon, DocumentIcon, EditIcon, Header, LightTooltip, LinkIcon, OverflowTip, PageTitle, QrCodeIcon, RoundBtnIcon, SelectControl } from '../../components';
import { getTranslation, PATH_IB_LINK_STATISTICS } from '../../utils';
import { ClientLink, Link, LinkPartnershipType, PartnershipLink } from './RegistrationLinksModel';
import { PartnerPartnership } from '../../models';
import { getPartnerPartnerships } from '../IBDashboard';
import { DefaultLinkDialog, EditLinkDialog, PartnershipLinkDialog, QuickReport, QuickReportWithPercentage } from './components';
import { QrCodeDialog } from './components/QrCodeDialog';

export const RegistrationLinks: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();
  const history = useHistory();
  const { translations, partnershipList, savePartnershipList } = useAppData();
  const [links, setLinks] = useState<Link[]>([]);
  const [filteredLinks, setFilteredLinks] = useState<Link[]>([]);
  const [expandedLink, setExpandedLink] = useState<string | false>(false);
  const [expandedLinkInfo, setExpandedLinkInfo] = useState<string | false>(false);
  const [translation, setTranslation] = useState<any>([]);
  const [defaultLink, setDefaultLink] = useState<Link>();
  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [dialogTrigger, setDialogTrigger] = useState<boolean>(false);
  const [canCreateDefaultLink, setCanCreateDefaultLink] = useState<boolean>(false);
  const [selectedPartnership, setSelectedPartnership] = useState<PartnerPartnership>();
  const [selectedPartnershipCode, setSelectedPartnershipCode] = useState<string>('All');
  const [selectedLinkType, setSelectedLinkType] = useState<string>('All');
  const [openDefaultLinkDialog, setOpenDefaultLinkDialog] = useState<boolean>(false);
  const [selectedLink, setSelectedLink] = useState<Link>();
  const [openQrCodeDialog, setOpenQrCodeDialog] = useState<boolean>(false);
  const [openEditLinkDialog, setOpenEditLinkDialog] = useState<boolean>(false);

  const getPartnershipData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPartnerPartnerships(true);
      setPartnerships(response.data);
      savePartnershipList(JSON.stringify(response.data));
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  }, [addError, setIsLoading, savePartnershipList]);

  useEffect(() => {
    if (partnershipList !== undefined) {
      setPartnerships(JSON.parse(partnershipList));
    } else {
      (async () => {
        await getPartnershipData();
      })();
    }
  }, [partnershipList, getPartnershipData]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const getData = useCallback(
    async (partnership?: PartnerPartnership) => {
      setIsLoading(true);
      const linksArr: Link[] = [];
      try {
        if (partnership) {
          if (partnership.partnershipType?.name === 'IB' || partnership.partnershipType?.name === 'MLM') {
            const { links, linkSetup } = await Promise.all([getClientLinks(partnership.id), getClientLinkSetup(partnership.id)]).then(([{ data: links }, { data: linkSetup }]) => ({
              links,
              linkSetup,
            }));

            linksArr.push(...links.links);
            setCanCreateDefaultLink(linkSetup.canCreateDefaultLink);

            if (partnership.partnershipType?.name === 'MLM') {
              const response = await getSubPartnershipLinks(partnership.id);
              linksArr.push(...response.data.links);
            }
          } else {
            const response = await getSubPartnershipLinks(partnership.id);
            linksArr.push(...response.data.links);
          }
        } else {
          const { clientLinks, partnershipLinks } = await Promise.all([getAllClientLinks(), getAllSubPartnershipLinks()]).then(([{ data: clientLinks }, { data: partnershipLinks }]) => ({
            clientLinks,
            partnershipLinks,
          }));
          linksArr.push(...clientLinks.links);
          linksArr.push(...partnershipLinks.links);
        }
        setLinks(linksArr);
        setFilteredLinks(linksArr);
      } catch (error) {
        addError(JSON.stringify(error.response));
        if (error.response.data.code === 'MlmLinks.NotAllowed') {
          setIsError(true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [addError, setIsLoading],
  );

  useEffect(() => {
    if (!isError) {
      (async () => {
        await getData();
      })();
    }
  }, [isError]);

  useEffect(() => {
    if (links.length > 0) {
      const link = links.find((l) => l.isDefault);
      if (link) {
        setDefaultLink(link);
      }
    } else {
      setDefaultLink(undefined);
    }
  }, [links]);

  const handlePartnershipChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const selected = event.target.value;
      if (selected !== 'All') {
        const partnership = partnerships?.find((p) => p.partnershipCode === event.target.value);

        if (partnership) {
          setSelectedPartnership(partnership);
          setSelectedPartnershipCode(partnership.partnershipCode);
          setSelectedLinkType('All');
          await getData(partnership);
        }
      } else {
        setSelectedPartnership(undefined);
        setSelectedPartnershipCode('All');
        setSelectedLinkType('All');
        await getData();
      }
    },
    [partnerships],
  );

  const handleLinkTypeChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const selected = event.target.value;
      if (selected !== 'All') {
        const filtered: Link[] = [];
        links.forEach((link) => {
          if (selected === 'Links for sub-partners' && link.partnershipLinkDetails) {
            filtered.push(link);
          }
          if (selected === 'Links for clients' && link.partnershipClientShortLinkDetails) {
            filtered.push(link);
          }
        });
        setFilteredLinks(filtered);
        setSelectedLinkType(selected);
      } else {
        setSelectedLinkType('All');
        setFilteredLinks(links);
      }
    },
    [links],
  );

  const handleLinkCopy = useCallback(
    async (event, link) => {
      event.stopPropagation();
      await navigator.clipboard.writeText(link);
      enqueueSnackbar(translation.text_1637, { variant: 'success' });

      const eventProperties = {
        linkType: 'link copied',
      };
      trackAmplitudeData('partnerPortal_action_regLinkCopy', eventProperties);
    },
    [enqueueSnackbar, translation.text_1637],
  );

  const handleExpandedAccordionChange = (link: string) => (event: any, isExpanded: boolean) => {
    setExpandedLink(isExpanded ? link : false);
  };

  const handleShowDetailsChange = (link: string) => {
    setExpandedLinkInfo(expandedLinkInfo !== link ? link : false);
  };

  const generateDefaultLink = useCallback(
    async (linkName: string, partnership: PartnerPartnership) => {
      setIsLoading(true);

      try {
        const response = await createDefaultLink(partnership.id, linkName);
        const eventProperties = {
          linkType: 'Default link',
        };
        trackAmplitudeData('partnerPortal_action_regLinkCreation', eventProperties);

        const link = response.data;
        link.isDefault = true;
        setDefaultLink(link);
        setLinks([link, ...links]);
        setFilteredLinks([link, ...links]);
        setExpandedLink(link.link);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
        setOpenDefaultLinkDialog(false);
      }
    },
    [addError, selectedPartnership, setIsLoading],
  );

  const handleClose = (clientLink?: ClientLink, partnershipLink?: PartnershipLink, mlmLink?: PartnershipLink) => {
    if (clientLink) {
      setLinks([clientLink, ...links]);
      setFilteredLinks([clientLink, ...links]);
      setExpandedLink(clientLink.link);
    }
    if (partnershipLink) {
      setLinks([partnershipLink, ...links]);
      setFilteredLinks([partnershipLink, ...links]);
      setExpandedLink(partnershipLink.link);
    }
    if (mlmLink) {
      setLinks([mlmLink, ...links]);
      setFilteredLinks([mlmLink, ...links]);
      setExpandedLink(mlmLink.link);
    }
    setDialogTrigger(false);
    window.scrollTo(0, 0);
  };

  const editLinkName = useCallback(
    async (id: string, name: string, isClientLink: boolean) => {
      setIsLoading(true);

      try {
        if (isClientLink) {
          await updateClientLinkName(id, name);
        } else {
          await updatePartnerLinkName(id, name);
        }

        await getData(selectedPartnership);
      } catch (error) {
        if (error.response.data.errors && error.response.data.errors.LinkName) {
          enqueueSnackbar(error.response.data.errors.LinkName[0].message, {
            variant: 'error',
            preventDuplicate: true,
          });
        } else {
          addError(JSON.stringify(error.response));
        }
      } finally {
        setIsLoading(false);
        setOpenEditLinkDialog(false);
      }
    },
    [addError, selectedPartnership, setIsLoading],
  );

  const redirectToIBLinkReport = useCallback(
    (link: Link) => {
      history.push({
        pathname: PATH_IB_LINK_STATISTICS,
        state: {
          data: link,
        },
      });
    },
    [history],
  );

  return (
    <>
      <Header title={translation.text_6232} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6232} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <Grid container className={classes.filterContainer} spacing={2}>
            <Grid item className={classes.filters}>
              {partnerships && partnerships.length !== 0 && (
                <FormControl variant="outlined" className={commonClasses.formControl}>
                  <p className={commonClasses.formControlTitle}>{translation.text_6275}</p>
                  <SelectControl value={selectedPartnershipCode} onChange={handlePartnershipChange} minWidth={97}>
                    <MenuItem className={commonClasses.optionSelector} value="All">
                      {translation.show_all}
                    </MenuItem>
                    {partnerships.map((partnership) => (
                      <MenuItem className={commonClasses.optionSelector} style={{ width: 'inherit' }} key={partnership.id} value={partnership.partnershipCode}>
                        {partnership.partnershipCode}
                      </MenuItem>
                    ))}
                  </SelectControl>
                </FormControl>
              )}
            </Grid>
            <Grid item className={classes.filters}>
              <FormControl variant="outlined" className={commonClasses.formControl}>
                <p className={commonClasses.formControlTitle}>{translation.link_type}</p>
                <SelectControl value={selectedLinkType} onChange={handleLinkTypeChange} minWidth={97}>
                  <MenuItem className={commonClasses.optionSelector} value="All">
                    {translation.show_all}
                  </MenuItem>
                  <MenuItem className={commonClasses.optionSelector} style={{ width: 'inherit' }} key="Links for clients" value="Links for clients">
                    {translation.links_for_clients}
                  </MenuItem>
                  <MenuItem className={commonClasses.optionSelector} style={{ width: 'inherit' }} key="Links for sub-partners" value="Links for sub-partners">
                    {translation.links_for_subpartners}
                  </MenuItem>
                </SelectControl>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} className={commonClasses.header}>
            {translation.generated_links}
          </Grid>
          <Grid item xs={12} className={commonClasses.contentWithAccordion}>
            {filteredLinks.map((link) =>
              link.partnershipClientShortLinkDetails ? (
                <Fragment key={link.shortCode}>
                  <Accordion classes={{ root: commonClasses.accordion }} expanded={expandedLink === link.link} onChange={handleExpandedAccordionChange(link.link)}>
                    <AccordionSummary classes={{ content: commonClasses.accordionContent }} expandIcon={<ExpandLess />}>
                      <Grid item>
                        <LinkIcon />
                      </Grid>
                      <Grid container direction="column" style={{ marginInlineStart: 10 }}>
                        <Typography className={commonClasses.accordionHeader}>{link.partnershipClientShortLinkDetails.linkName}</Typography>
                        <Grid container direction="row" alignItems="center">
                          <Typography className={commonClasses.accordionSubHeader}>
                            {link.link.length > 20 ? (
                              <LightTooltip title={link.link} arrow placement="top-start">
                                <span>{link.link.substring(0, 90)}...</span>
                              </LightTooltip>
                            ) : (
                              link.link
                            )}
                          </Typography>
                          <Button onClick={(event) => handleLinkCopy(event, link.link)} className={classes.copyButtons}>
                            <CopyIcon />
                          </Button>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid item xs={12}>
                        <Grid container direction="row" spacing={2} className={classes.panels}>
                          {/* <Grid item className={classes.quickReports}> */}
                          {/*   <QuickReport title={translation.clicks} tooltip={translation.clicks_tooltip} value={link.clicks ? link.clicks : 0} /> */}
                          {/* </Grid> */}
                          <Grid item className={classes.quickReports}>
                            <QuickReportWithPercentage
                              translation={translation}
                              title={translation.registrations}
                              value={link.registrationsCount ? link.registrationsCount : 0}
                              percentage={link.registrationsPercentage ? link.registrationsPercentage : 0}
                            />
                          </Grid>
                          <Grid item className={classes.quickReports}>
                            <QuickReportWithPercentage
                              translation={translation}
                              title={translation.kyc_verified}
                              tooltip={translation.kyc_verified_tooltip}
                              value={link.fullKycCount ? link.fullKycCount : 0}
                              percentage={link.fullKycPercentage ? link.fullKycPercentage : 0}
                            />
                          </Grid>
                          <Grid item className={classes.quickReports}>
                            <QuickReportWithPercentage
                              translation={translation}
                              title={translation.first_time_deposits}
                              value={link.firstTimeFundsInCount ? link.firstTimeFundsInCount : 0}
                              percentage={link.firstTimeFundsInPercentage ? link.firstTimeFundsInPercentage : 0}
                            />
                          </Grid>
                          <Grid item className={classes.quickReports}>
                            <QuickReportWithPercentage
                              translation={translation}
                              title={translation.clients_started_trading}
                              value={link.startedTradingCount ? link.startedTradingCount : 0}
                              percentage={link.startedTradingPercentage ? link.startedTradingPercentage : 0}
                            />
                          </Grid>
                          <Grid item className={classes.quickReports}>
                            <QuickReport title={translation.active_trading_accounts} tooltip={translation.active_trading_accounts_tooltip} value={link.tradingAccountsActiveCount ? link.tradingAccountsActiveCount : 0} />
                          </Grid>
                          <Grid item className={classes.quickReports}>
                            <QuickReport title={translation.inactive_trading_accounts} tooltip={translation.inactive_trading_accounts_tooltip} value={link.tradingAccountsInactiveCount ? link.tradingAccountsInactiveCount : 0} />
                          </Grid>
                        </Grid>
                        <Grid container direction="column">
                          <Grid item xs={12} className={classes.showInfoRow}>
                            {expandedLinkInfo !== link.link && (
                              <Button
                                className={commonClasses.editBtn}
                                onClick={() => {
                                  handleShowDetailsChange(link.link);
                                }}
                                endIcon={<ExpandMore />}
                              >
                                {translation.show_info}
                              </Button>
                            )}
                          </Grid>
                          {expandedLinkInfo === link.link && (
                            <Grid container direction="column">
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6188}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {translation.client_link}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.generated}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.partnershipClientShortLinkDetails.creationDate}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6275}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.partnershipClientShortLinkDetails.partnershipCode}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6270}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.brokerName}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_531}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    <OverflowTip title={link.partnershipClientShortLinkDetails.accountType === '' ? translation.any : link.partnershipClientShortLinkDetails.accountType}>
                                      <span style={{ textAlign: 'right' }}>{link.partnershipClientShortLinkDetails.accountType === '' ? translation.any : link.partnershipClientShortLinkDetails.accountType}</span>
                                    </OverflowTip>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_423}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.partnershipClientShortLinkDetails.currency === '' ? translation.any : link.partnershipClientShortLinkDetails.currency}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6272}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.shortCode}
                                  </Grid>
                                </Grid>
                              </Grid>
                              {link.partnershipClientShortLinkDetails.masterCode && (
                                <Grid item xs={12} className={classes.detailsRow}>
                                  <Grid container direction="row" className={classes.detailsItem}>
                                    <Grid item xs={6} className={classes.columnName}>
                                      {translation.master_code}
                                    </Grid>
                                    <Grid item xs={6} className={classes.columnValue}>
                                      {link.partnershipClientShortLinkDetails.masterCode}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}
                              {link.partnershipClientShortLinkDetails?.salesCode && (
                                <Grid item xs={12} className={classes.detailsRow}>
                                  <Grid container direction="row" className={classes.detailsItem}>
                                    <Grid item xs={6} className={classes.columnName}>
                                      {translation.sales_code}
                                    </Grid>
                                    <Grid item xs={6} className={classes.columnValue}>
                                      {link.partnershipClientShortLinkDetails?.salesCode}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.flowkey}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.partnershipClientShortLinkDetails.flowkey}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6682}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.language}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_432}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {getTranslation(translation, link.partnershipClientShortLinkDetails.country || '')}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.showInfoRow}>
                                <Button
                                  className={commonClasses.editBtn}
                                  endIcon={<ExpandLess />}
                                  onClick={() => {
                                    setExpandedLinkInfo(false);
                                  }}
                                >
                                  {translation.hide_info}
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <Grid container direction="row">
                            <Button
                              startIcon={<QrCodeIcon />}
                              className={commonClasses.editBtn}
                              onClick={() => {
                                setSelectedLink(link);
                                setOpenQrCodeDialog(true);
                              }}
                            >
                              {translation.qr_code}
                            </Button>
                            <Button startIcon={<DocumentIcon />} className={commonClasses.editBtn} onClick={() => redirectToIBLinkReport(link)}>
                              {translation.detailed_report}
                            </Button>
                            <Button
                              startIcon={<EditIcon />}
                              className={commonClasses.editBtn}
                              onClick={() => {
                                setSelectedLink(link);
                                setOpenEditLinkDialog(true);
                              }}
                            >
                              {translation.text_471}
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Fragment>
              ) : (
                <Fragment key={link.shortCode}>
                  <Accordion classes={{ root: commonClasses.accordion }} expanded={expandedLink === link.link} onChange={handleExpandedAccordionChange(link.link)}>
                    <AccordionSummary classes={{ content: commonClasses.accordionContent }} expandIcon={<ExpandLess />}>
                      <Grid item>
                        <LinkIcon />
                      </Grid>
                      <Grid container direction="column" style={{ marginInlineStart: 10 }}>
                        <Typography className={commonClasses.accordionHeader}>{link.partnershipLinkDetails?.linkName}</Typography>
                        <Grid container direction="row" alignItems="center">
                          <Typography className={commonClasses.accordionSubHeader}>
                            {link.link.length > 20 ? (
                              <LightTooltip title={link.link} arrow placement="top-start">
                                <span>{link.link.substring(0, 90)}...</span>
                              </LightTooltip>
                            ) : (
                              link.link
                            )}
                          </Typography>
                          <Button onClick={(event) => handleLinkCopy(event, link.link)} className={classes.copyButtons}>
                            <CopyIcon />
                          </Button>
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid item xs={12}>
                        <Grid container direction="column">
                          <Grid item xs={12} className={classes.showInfoRow}>
                            {expandedLinkInfo !== link.link && (
                              <Button
                                className={commonClasses.editBtn}
                                onClick={() => {
                                  handleShowDetailsChange(link.link);
                                }}
                                endIcon={<ExpandMore />}
                              >
                                {translation.show_info}
                              </Button>
                            )}
                          </Grid>
                          {expandedLinkInfo === link.link && (
                            <Grid container direction="column">
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6188}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.type === LinkPartnershipType.Mlm ? translation.mlm_link : translation.sub_partnership_link}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.generated}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.creationDate}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_6275}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.partnershipCode}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_435}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.brokerName}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_436}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.language}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_437}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.shortCode}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.detailsRow}>
                                <Grid container direction="row" className={classes.detailsItem}>
                                  <Grid item xs={6} className={classes.columnName}>
                                    {translation.text_438}
                                  </Grid>
                                  <Grid item xs={6} className={classes.columnValue}>
                                    {link.percentage}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} className={classes.showInfoRow}>
                                <Button
                                  className={commonClasses.editBtn}
                                  endIcon={<ExpandLess />}
                                  onClick={() => {
                                    setExpandedLinkInfo(false);
                                  }}
                                >
                                  {translation.hide_info}
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Grid container direction="row">
                              <Button
                                startIcon={<QrCodeIcon />}
                                className={commonClasses.editBtn}
                                onClick={() => {
                                  setSelectedLink(link);
                                  setOpenQrCodeDialog(true);
                                }}
                              >
                                {translation.qr_code}
                              </Button>
                              <Button startIcon={<DocumentIcon />} className={commonClasses.editBtn} onClick={() => redirectToIBLinkReport(link)}>
                                {translation.detailed_report}
                              </Button>
                              <Button
                                startIcon={<EditIcon />}
                                className={commonClasses.editBtn}
                                onClick={() => {
                                  setSelectedLink(link);
                                  setOpenEditLinkDialog(true);
                                }}
                              >
                                {translation.text_471}
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Fragment>
              ),
            )}
          </Grid>
          {canCreateDefaultLink && defaultLink === undefined && (
            <Grid item xs={12}>
              <Grid container direction="row" alignItems="center" style={{ padding: 16, height: 88 }}>
                <Grid item>
                  <Button onClick={() => setOpenDefaultLinkDialog(true)} style={{ padding: 0, minWidth: 'unset' }}>
                    <RoundBtnIcon />
                  </Button>
                </Grid>
                <Grid item style={{ marginInlineStart: 8 }}>
                  <Grid container direction="column">
                    <Typography className={commonClasses.accordionHeader}>{translation.create_default_link}</Typography>
                    <Grid container direction="row" alignItems="center">
                      <Typography className={commonClasses.accordionSubHeader}>{translation.create_default_link_tooltip}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container direction="row" alignItems="center" style={{ padding: 16, height: 88 }}>
              <Grid item>
                <Button onClick={() => setDialogTrigger(true)} style={{ padding: 0, minWidth: 'unset' }}>
                  <RoundBtnIcon />
                </Button>
              </Grid>
              <Grid item style={{ marginInlineStart: 8 }}>
                <Grid container direction="column">
                  <Typography className={commonClasses.accordionHeader}>{translation.customise_link}</Typography>
                  <Grid container direction="row" alignItems="center">
                    <Typography className={commonClasses.accordionSubHeader}>{translation.customise_link_tooltip}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {openDefaultLinkDialog && (
          <DefaultLinkDialog open={openDefaultLinkDialog} partnerships={partnerships} partnership={selectedPartnership} translation={translation} onCreateLink={generateDefaultLink} onClose={() => setOpenDefaultLinkDialog(false)} />
        )}
        {dialogTrigger && <PartnershipLinkDialog partnerships={partnerships} handleClose={handleClose} open={dialogTrigger} partnership={selectedPartnership} />}
        {openQrCodeDialog && selectedLink && <QrCodeDialog open={openQrCodeDialog} linkUrl={selectedLink?.link || ''} translation={translation} onClose={() => setOpenQrCodeDialog(false)} />}
        {selectedLink && <EditLinkDialog open={openEditLinkDialog} link={selectedLink} translation={translation} onClose={() => setOpenEditLinkDialog(false)} onEditLink={editLinkName} />}
      </Grid>
    </>
  );
};
